import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { post } from '../../services/api';

export interface AnalyticsEvent {
  event: string;
  props?: Record<string, unknown>;
  ts: number;
  path: string;
}

interface AnalyticsContextValue {
  track: (event: string, props?: Record<string, unknown>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

const FLUSH_INTERVAL_MS = 5000;

async function sendBatch(events: AnalyticsEvent[]): Promise<void> {
  if (events.length === 0) return;
  try {
    await post('/analytics/events/', { events });
  } catch {
    void 0;
  }
}

function sendBeacon(events: AnalyticsEvent[]): void {
  if (events.length === 0) return;
  try {
    const url = (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1';
    const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(`${url}/analytics/events/`, blob);
    }
  } catch {
    void 0;
  }
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const queueRef = useRef<AnalyticsEvent[]>([]);
  const location = useLocation();

  const flush = useCallback(() => {
    if (queueRef.current.length === 0) return;
    const events = queueRef.current.splice(0, queueRef.current.length);
    void sendBatch(events);
  }, []);

  const track = useCallback((event: string, props?: Record<string, unknown>) => {
    queueRef.current.push({
      event,
      props,
      ts: Date.now(),
      path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, []);

  useEffect(() => {
    const id = window.setInterval(flush, FLUSH_INTERVAL_MS);
    const handler = () => sendBeacon(queueRef.current.splice(0, queueRef.current.length));
    window.addEventListener('beforeunload', handler);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('beforeunload', handler);
      flush();
    };
  }, [flush]);

  useEffect(() => {
    track('page_view', { path: location.pathname + location.search });
  }, [location.pathname, location.search, track]);

  const value = useMemo<AnalyticsContextValue>(() => ({ track }), [track]);
  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error('useAnalytics must be used inside an <AnalyticsProvider>');
  }
  return ctx;
}
